import {CancellationPenalties} from '@findhotel/sapi'
import {isFuture} from 'date-fns/isFuture'
import {subHours} from 'date-fns/subHours'

import {getPersistedConfiguration} from '../services/offerConfigurationApi/store'
import {CancellationPenalty} from '../types/offer'
import {
  getCancellationDeadline,
  getHasFreeCancellation
} from './cancellationPenalties'

const HOURS_IN_DAY = 24

export const DEFAULT_VIO_CANCELLATION_WINDOW_DAYS = 3
// Keep in sync with BoVio dealFreeze.userMaxFreezeLimit and userMaxMonthlyFreezeLimit.
export const DEFAULT_USER_MAX_FREEZE_LIMIT = 10
export const DEFAULT_USER_MAX_MONTHLY_FREEZE_LIMIT = 20

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && value > 0

/**
 * Derives when a deal freeze would expire. The offer check owns `expiresAt`,
 * but it needs a JWT, so guests never get one.
 * @param cancellationPenalties - Source of the free-cancellation deadline the
 * expiry counts back from; without one there is nothing to derive.
 * @returns The derived expiry, or undefined when it cannot be derived.
 */
export const calculateFreezeExpiry = (
  cancellationPenalties?: CancellationPenalty[] | CancellationPenalties[]
) => {
  if (!cancellationPenalties?.length) return undefined

  if (!getHasFreeCancellation(cancellationPenalties)) return undefined

  const cancellationDeadline = getCancellationDeadline(cancellationPenalties)
  if (!cancellationDeadline) return undefined

  const {vioCancellationWindowDays} =
    getPersistedConfiguration().dealFreeze || {}

  // Unvalidated session-storage JSON, so null and 0 have to fall back too.
  const windowDays = isPositiveNumber(vioCancellationWindowDays)
    ? vioCancellationWindowDays
    : DEFAULT_VIO_CANCELLATION_WINDOW_DAYS

  const expiry = subHours(
    new Date(cancellationDeadline),
    windowDays * HOURS_IN_DAY
  )

  return isFuture(expiry) ? expiry : undefined
}

export const getFreezeLimits = () => {
  const {userMaxFreezeLimit, userMaxMonthlyFreezeLimit} =
    getPersistedConfiguration().dealFreeze || {}

  return {
    atOnce: isPositiveNumber(userMaxFreezeLimit)
      ? userMaxFreezeLimit
      : DEFAULT_USER_MAX_FREEZE_LIMIT,
    monthly: isPositiveNumber(userMaxMonthlyFreezeLimit)
      ? userMaxMonthlyFreezeLimit
      : DEFAULT_USER_MAX_MONTHLY_FREEZE_LIMIT
  }
}
